/* eslint-disable max-lines */
import { logger } from '@adornis/base/logging.js';
import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { type DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/forms/x-plain-textarea.js';
import { openPopupWithLoadingScreen } from '@adornis/print/client/safe-popup.js';
import { APrint } from '@adornis/print/db/print.js';
import '@chancenlogistik/client/components/logistik-component-edit-chancen-brief-variables';
import '@chancenlogistik/client/components/logistik-component-error-message';
import '@chancenlogistik/client/components/logistik-edit-order-item';
import {
  LogistikEntireTrip,
  LogistikIndividualOrderItem,
  LogistikLearningMaterialOrderItem,
  LogistikOrder,
  LogistikOrderChancenBriefItem,
  LogistikOrderClearanceState,
  LogistikOrderItem,
  LogistikOtherOrder,
} from '@chancenwerk/db/LogistikOrder.js';
import {
  LogistikOrderDeliveryStateEnum,
  LogistikShippingMethod,
  LogistikShippingRecipient,
  LogistikShippingStatus,
} from '@chancenwerk/db/enums/LogistikEnums.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { ChancenRoleAspect } from '@chancenwerk/db/semantics/ChancenRoleAspect.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/cr-link.js';
import '@chancenwerk/design/components/cr-loader.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/cr-textarea.js';
import '@chancenwerk/design/components/cr-tooltip.js';
import '@chancenwerk/design/components/fonts.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateTime } from 'luxon';
import { of, switchMap } from 'rxjs';
import { LogistikDialogOrderReminder } from './logistik-dialog-order-reminder.js';
import { LogistikDialogSendCustomerEmail } from './logistik-dialog-send-customer-email.js';
import { LogistikDialogSplitOrder } from './logistik-dialog-split-order.js';
import {
  acceptIndividualOrder,
  declineIndividualOrder,
  moveOrderBackToReview,
} from './logistik-dialogs-accept-decline-invidivdual-order.js';

const editButton = (cb: () => void): Renderable => html`
  <cr-button mode="icon" icon="edit" @click=${() => cb()}> </cr-button>
`;
const doneButton = (cb: () => void): Renderable => html`
  <cr-button mode="icon" icon="check" @click=${() => cb()}> </cr-button>
`;

/**
 * @customElement logistik-dialog-edit-order
 */
@customElement('logistik-dialog-edit-order')
export class LogistikDialogEditOrder extends ChromiumDialog<void> {
  static override get element_name() {
    return 'logistik-dialog-edit-order';
  }

  override heading = 'Bestellung bearbeiten';

  override styles() {
    return [...super.styles(), { p: { margin: 0 } }];
  }

  @property({ attribute: false }) orderID = new RXController<Maybe<string>>(this, undefined);
  @state() public order = new RXController(this, this.orderID.observable, orderID$ =>
    orderID$.pipe(
      switchMap(orderID =>
        orderID ? LogistikOrder.getByID<LogistikOrder>(orderID)(LogistikOrder.allFields) : of(null),
      ),
    ),
  );

  @state() private readonly _errorMsg = new RXController(this, '');

  private readonly _form = new FormController(this, this.order.observable, {
    success: order => this._handleFormSuccess(order),
    error: err => this._handleFormError(err),
  });

  private async _handleFormSuccess(order: LogistikOrder) {
    this._errorMsg.next('');
    if (order.status !== LogistikShippingStatus.NEW && order.orderNumber === '') {
      order.status = LogistikShippingStatus.NEW;
      this._errorMsg.next('Bitte Auftragsnummer angeben');
      return;
    }
    if (
      order.status === LogistikShippingStatus.SHIPPED &&
      !order.trackingLink &&
      order.shippingMethod !== LogistikShippingMethod.MAIL &&
      order.shippingMethod !== LogistikShippingMethod.OTHER_NO_TRACKING &&
      order.shippingMethod !== LogistikShippingMethod.EMAIL
    ) {
      order.status = LogistikShippingStatus.PROCESSING;
      this._errorMsg.next('Bitte Tracking-Link angeben');
      return;
    }

    if (
      order.status === LogistikShippingStatus.COMPLETED &&
      order.deliveryStatus?.status !== LogistikOrderDeliveryStateEnum.RECEIVED &&
      !(await ChromiumDialog.confirm(
        'Der Erhalt dieser Bestellung wurde noch nicht bestätigt, soll die Bestellung trotzdem als abgeschlossen markiert werden?',
      ))
    ) {
      this._errorMsg.next('Der Erhalt der Bestellung muss zunächst bestätigt werden');
      return;
    }

    await order.save();
    void ChromiumSnackbar.show('Auftrag erfolgreich gespeichert');
  }

  private _handleFormError(err: Error) {
    logger.error({ err }, 'Es gab ein Problem beim Speichern der Bestellung');
    void ChromiumSnackbar.show(
      'Es gab ein Problem beim Speichern der Bestellung. Bitte versuchen Sie es später erneut',
    );
  }

  override content() {
    if (this.order.isLoading)
      return html` <x-flex h100 w100 center crossaxis-center flex> <cr-loader></cr-loader> </x-flex> `;
    if (!this.order.value)
      return html`
        <x-empty-placeholder no-wrapper .img=${'close'} .text=${'Keine Bestellung gefunden'}></x-empty-placeholder>
      `;
    return html`
      <cr-flex space="md" padding="lg">
        <cr-flex horizontal space="sm" space-between crossaxis-center>
          <cr-text bold>Bearbeiter: </cr-text>
          ${this._processorPicker()} ${this._deliveryConfirmationButton()} ${this._emailButton()}
          ${this._notificationButton()}
        </cr-flex>
        ${this._getOrderErrorMessages()}
        <cr-flex horizontal space="md">
          <cr-flex flex space="md">
            <cr-h3>Inhalt</cr-h3>
            ${this._orderItemList()}
          </cr-flex>
          <cr-flex flex space="md" ${css({ minWidth: '390px' })}>
            <cr-h3 ${css({ gridColumn: '1 / -1' })}>Details</cr-h3>
            ${this._getDetailsGrid()}
          </cr-flex>
        </cr-flex>
        ${this.order.value.clearanceState !== LogistikOrderClearanceState.NEEDS_CLEARANCE &&
        this.order.value.clearanceState !== LogistikOrderClearanceState.DECLINED
          ? html`
              <cr-flex space="md">
                <cr-h3>Status</cr-h3>
                ${this._errorMsg.value
                  ? html`<logistik-component-error-message>${this._errorMsg.value}</logistik-component-error-message>`
                  : nothing}
                ${this.order.value.status === LogistikShippingStatus.SHIPPED ||
                this.order.value.status === LogistikShippingStatus.COMPLETED
                  ? this._getDeliveryStatus()
                  : nothing}
                <cr-input ${this._form.field('orderNumber')} placeholder="Auftragsnummer"></cr-input>
                <cr-dropdown-selection
                  ${this._form.field('status')}
                  placeholder="Status"
                  .selectables=${Object.values(LogistikShippingStatus)}
                >
                </cr-dropdown-selection>
                <cr-dropdown-selection
                  ${this._form.field('shippingMethod')}
                  placeholder="Versandart"
                  .selectables=${Object.values(LogistikShippingMethod)}
                >
                </cr-dropdown-selection>
                <cr-input ${this._form.field('trackingLink')} placeholder="Tracking Link"></cr-input>
                <x-flex horizontal crossaxis-start space="md">
                  <cr-icon ${css({ color: this.colors.accent, fontSize: this.spacing.xl })}> description </cr-icon>
                  <cr-textarea flex placeholder="Notizen" ${this._form.field('notes')}></cr-textarea>
                </x-flex>
                <cr-flex horizontal space="sm" ${css({ placeContent: 'flex-end' })}>
                  <cr-flex>
                    ${!this.order.value.orderNumber
                      ? html`
                          <cr-tooltip
                            .text=${html` <cr-text> Fügen Sie zunächst eine Auftragsnummer hinzu </cr-text> `}
                          >
                          </cr-tooltip>
                        `
                      : nothing}
                    ${this._splitOrderButton()}
                  </cr-flex>
                </cr-flex>
              </cr-flex>
            `
          : nothing}
      </cr-flex>
    `;
  }

  private _processorPicker(): Renderable {
    return html`
      <chancen-component-semantic-user-picker
        ${css({ flex: '1', alignItems: 'center' })}
        placeholder="Bearbeiter auswählen"
        .filter=${new ChancenwerkUserFilter({
          aspects: [
            new ChancenRoleAspect({ value: Role.LOGISTICS_STAFF }),
            new BooleanAspect({ customID: 'active', value: true }),
          ],
        })}
        .valueID=${this.order.value!.processorID}
        @value-picked=${(e: ValueEvent<Maybe<ChancenwerkUser>>) => {
          if (!this.order.value) throw new Error('Order should not be None');
          this.order.value.processorID = e.detail.value?._id ?? null;
          this.requestUpdate();
        }}
      ></chancen-component-semantic-user-picker>
    `;
  }

  private _deliveryConfirmationButton(): Renderable {
    return html`
      <cr-button
        icon="qr_code"
        mode="outline"
        @click=${() => {
          if (!this.order.value) throw new Error('Order should not be None');
          return openPopupWithLoadingScreen(
            APrint.servePdfFromComponent(
              'chancen-order-delivery-confirm-qrcode',
              new URLSearchParams({
                orderID: this.order.value._id,
                origin: baseConfig.get('ROOT_URL'),
              }).toString(),
            )(),
          );
        }}
      >
        Empfangsbestätigung
      </cr-button>
    `;
  }

  private _emailButton(): Renderable {
    return html`
      <cr-button
        icon="email"
        mode="outline"
        @click=${async () =>
          LogistikDialogSendCustomerEmail.showPopup<boolean>({
            props: { logistikOrder: this.order.value },
          }).catch(() => undefined)}
      >
        Email
      </cr-button>
    `;
  }

  private _notificationButton(): Renderable {
    return html`
      <cr-button
        icon="notifications"
        mode="outline"
        @click=${() => {
          void LogistikDialogOrderReminder.showPopup({ props: { orderID: this.orderID.value } }).catch(() => {});
        }}
      >
        Benachrichtigung
      </cr-button>
    `;
  }

  private _splitOrderButton(): Renderable {
    return html`
      <cr-button
        ?disabled=${!this.order.value!.orderNumber}
        mode="outline"
        @click=${async () => {
          await LogistikDialogSplitOrder.showPopup({ modal: true, props: { order: this.order.value } });
          this.requestUpdate();
        }}
      >
        Bestellung aufteilen
      </cr-button>
    `;
  }

  private _orderItemList(): Renderable {
    return html`
      <cr-flex>
        ${this.order.value?.items.map(
          (item, i) =>
            html`
              <cr-flex
                horizontal
                crossaxis-center
                ${css({ background: i % 2 ? '#f1f1f1' : '#fafafa', paddingLeft: this.spacing.md })}
              >
                ${this._orderItemCheckbox(item)}
                <logistik-edit-order-item
                  flex
                  .order=${this.order.value}
                  .orderItem=${item}
                  .index=${i}
                  @delete-item=${() => {
                    this.order.value?.items.splice(i, 1);
                    this.requestUpdate();
                  }}
                ></logistik-edit-order-item>
              </cr-flex>
            `,
        )}
      </cr-flex>
    `;
  }

  private _orderItemCheckbox(
    item: LogistikIndividualOrderItem | LogistikEntireTrip | LogistikOtherOrder | LogistikOrderItem,
  ): Renderable {
    const itemHasCheckbox =
      (item._class !== LogistikLearningMaterialOrderItem._class ||
        !(item as LogistikLearningMaterialOrderItem).isDigital) &&
      (item._class !== LogistikOrderChancenBriefItem._class || !(item as LogistikOrderChancenBriefItem).isSelfPrint);
    return html`
      <cr-checkbox
        .value=${this.order.value?.checkedItems.includes(item._id)}
        .disabled=${!itemHasCheckbox}
        @value-picked=${(e: ValueEvent<boolean>) => {
          if (!this.order.value) return;
          const val = e.detail.value;
          const idx = this.order.value.checkedItems.indexOf(item._id);
          if (val && idx >= 0) {
            this.order.value.checkedItems.splice(idx, 1);
          } else if (!val && idx < 0) {
            this.order.value.checkedItems.push(item._id);
          }
        }}
        ${css({ marginBottom: this.spacing.sm, visibility: itemHasCheckbox ? 'visible' : 'hidden' })}
      >
      </cr-checkbox>
    `;
  }

  override footerContent() {
    if (!this.order.value) return nothing;
    const leftButton = html`
      <cr-flex start>
        <cr-button
          icon="delete"
          mode="outline"
          tone="error"
          @click=${async (e: MouseEvent) => {
            if (!this.order.value) return;
            const confirmation = await ChromiumDialog.confirm(
              `Möchten Sie diese Bestellung wirklich unwiderruflich löschen?`,
            ).catch(() => false);
            if (!confirmation) return;
            const removalResult = await this.order.value.remove();
            if (removalResult) void ChromiumSnackbar.show('Bestellung erfolgreich gelöscht');
            else void ChromiumDialog.alert('Fehler beim Löschen der Bestellung');
            void this.close();
          }}
        >
          Löschen
        </cr-button>
      </cr-flex>
    `;
    const rightButton =
      this.order.value.clearanceState !== LogistikOrderClearanceState.NEEDS_CLEARANCE &&
      this.order.value.clearanceState !== LogistikOrderClearanceState.DECLINED
        ? html`
            <cr-flex end>
              <cr-button
                @click=${(e: MouseEvent) =>
                  ChromiumDialog.waitFor(this._form.validate({ validateOmitted: false, mouseEvent: e }))}
              >
                Speichern
              </cr-button>
            </cr-flex>
          `
        : this.order.value.clearanceState !== LogistikOrderClearanceState.DECLINED
        ? html`
            <cr-flex horizontal end space="md">
              <cr-button
                icon="check"
                tone="success"
                @click=${async () => {
                  if (!this.order.value) throw new Error('Order should not be undefined/null');
                  if (await acceptIndividualOrder(this.order.value)) void this.close();
                }}
              >
                Freigeben
              </cr-button>
              <cr-button
                icon="block"
                tone="error"
                @click=${async () => {
                  if (!this.order.value) throw new Error('Order should not be undefined/null');
                  if (await declineIndividualOrder(this.order.value)) void this.close();
                }}
              >
                Ablehnen
              </cr-button>
            </cr-flex>
          `
        : html`
            <cr-flex horizontal end>
              <cr-button
                mode="outline"
                @click=${async () => {
                  if (!this.order.value) throw new Error('Order should not be undefined/null');
                  await moveOrderBackToReview(this.order.value);
                  this.requestUpdate();
                }}
              >
                Zurück in den Freigabeprozess
              </cr-button>
            </cr-flex>
          `;
    return html` <cr-flex horizontal space-between> ${leftButton} ${rightButton} </cr-flex>`;
  }

  private _getDeliveryStatus() {
    return html`
      <cr-flex horizontal space-between crossaxis-center space="sm">
        <cr-text>
          <b>Lieferstatus:</b> ${this.order.value?.deliveryStatus?.status}
          ${this.order.value?.deliveryStatus?.updatedAt
            ? `(zuletzt aktualisiert am
          ${this.order.value.deliveryStatus.updatedAt.toLocaleString(DateTime.DATETIME_SHORT)})`
            : ''}
        </cr-text>
        ${this._getOrderStatusButton()}
      </cr-flex>
    `;
  }

  private _getOrderStatusButton() {
    const order = this.order.value;
    if (!order) return nothing;
    switch (order.deliveryStatus?.status) {
      case LogistikOrderDeliveryStateEnum.RECEIVED:
        return nothing;
      case LogistikOrderDeliveryStateEnum.REQUESTED:
        break;
      // return html`<cr-button disabled>Status angefragt</cr-button>`;
      case LogistikOrderDeliveryStateEnum.NOT_RECEIVED:
        return html`
          <cr-button
            @click=${async () => {
              await LogistikOrder.sendDeliveryStatusRequestMail(order)();
              void ChromiumSnackbar.show('Anfrage wurde versendet');
            }}
          >
            Erneut anfragen
          </cr-button>
        `;
      case LogistikOrderDeliveryStateEnum.UNKNOWN:
        return html`
          <cr-button
            @click=${async () => {
              await LogistikOrder.sendDeliveryStatusRequestMail(order)();
              void ChromiumSnackbar.show('Anfrage wurde versendet');
            }}
          >
            Lieferstatus anfragen
          </cr-button>
        `;
      default:
        return nothing;
    }
  }

  private _getOrderErrorMessages() {
    if (!this.order.value) return nothing;
    const order = this.order.value;
    return html`
      ${order.splitOrderIDs?.length || order.splitFrom
        ? html`
            <logistik-component-error-message type="info">
              Diese Bestellung ist Teil einer Aufteilung
            </logistik-component-error-message>
          `
        : nothing}
      ${!order.shippingDetails.isEmailKnown
        ? order.accessTokenValid
          ? html`
              <logistik-component-error-message type="success">
                Diese Bestellung lief über den Zugangscode: ${order.accessToken}
              </logistik-component-error-message>
            `
          : html`
              <logistik-component-error-message type="warning">
                Die E-Mail des Auftraggebers ist unbekannt
              </logistik-component-error-message>
            `
        : nothing}
      ${order.isManuallyCreated
        ? html`
            <logistik-component-error-message type="info">
              Diese Bestellung wurde manuell angelegt
            </logistik-component-error-message>
          `
        : nothing}
      ${order.isExpress
        ? html`
            <logistik-component-error-message type="success">
              Expresslieferung angefragt
            </logistik-component-error-message>
          `
        : nothing}
    `;
  }

  @state() private _isEditingEmail = false;
  @state() private _isEditingShippingRecipient = false;
  @state() private _isEditingShippingInstructions = false;

  private _getDetailsGrid() {
    if (!this.order.value) return nothing;
    return html`
      <cr-grid columns="max-content 1fr" space="sm" ${css({ gridColumn: '1 / -1', justifyContent: 'space-between' })}>
        <cr-text>Bestelldatum:</cr-text>
        <cr-text>${this.order.value.createdAt.toFormat('dd.MM.yyyy')}</cr-text>
        <cr-text>Frist:</cr-text>
        <cr-text>${this.order.value.shippingDetails.dueDate.toFormat('dd.MM.yyyy')}</cr-text>

        ${this.order.value.clearanceFrom && this.order.value.clearanceState === LogistikOrderClearanceState.APPROVED
          ? html`
              <cr-text> Freigegeben durch: </cr-text> <cr-text> ${this.order.value.clearanceFrom.join(', ')} </cr-text>
            `
          : nothing}
        ${this.order.value.studentCount
          ? html`
              <cr-text> Schüler:innen Anzahl: </cr-text>
              <cr-text> ${this.order.value.studentCount} </cr-text>
            `
          : nothing}

        <cr-text bold ${css({ gridColumn: '1 / -1', fontSize: '1.2rem', marginTop: this.spacing.md })}>
          Auftraggeber
        </cr-text>
        <cr-flex horizontal crossaxis-center>
          <cr-text>E-Mail:</cr-text>
        </cr-flex>
        ${this._isEditingEmail
          ? html`
              <cr-flex horizontal crossaxis-center space="sm">
                <cr-input ${this._form.field('shippingDetails', 'customerEmail')}></cr-input>
                ${doneButton(() => (this._isEditingEmail = false))}
              </cr-flex>
            `
          : html`
              <cr-flex horizontal crossaxis-center space="sm">
                <cr-text>${this.order.value.shippingDetails.customerEmail}</cr-text>
                ${editButton(() => (this._isEditingEmail = true))}
              </cr-flex>
            `}
        <cr-text bold ${css({ gridColumn: '1 / -1', fontSize: '1.2rem', marginTop: this.spacing.md })}>
          Schule
        </cr-text>
        <cr-text>Schule:</cr-text>
        <cr-text>${this.order.value.shippingDetails.shippingSchool?.name ?? '-'}</cr-text>
        <cr-text>Adresse:</cr-text>
        <cr-text>${this.order.value.shippingDetails.shippingSchool?.address?.strasse ?? '-'}</cr-text>
        <cr-text>PLZ:</cr-text>
        <cr-text>${this.order.value.shippingDetails.shippingSchool?.address?.zipCity ?? '-'}</cr-text>
        <cr-text bold ${css({ gridColumn: '1 / -1', fontSize: '1.2rem', marginTop: this.spacing.md })}>
          Lieferadresse
        </cr-text>
        <cr-flex horizontal crossaxis-center>
          <cr-text>Zustellung an:</cr-text>
        </cr-flex>
        ${this._isEditingShippingRecipient
          ? html`
              <cr-flex horizontal crossaxis-center space="sm">
                <cr-dropdown-selection
                  ${this._form.field('shippingDetails', 'shippingRecipient')}
                  .selectables=${Object.values(LogistikShippingRecipient)}
                ></cr-dropdown-selection>
                ${doneButton(() => (this._isEditingShippingRecipient = false))}
              </cr-flex>
            `
          : html`
              <cr-flex horizontal crossaxis-center space="sm">
                <cr-text>${this.order.value.shippingDetails.shippingRecipient}</cr-text>
                ${editButton(() => (this._isEditingShippingRecipient = true))}
              </cr-flex>
            `}
        ${this._getOrderRecipientDetails()}
        <cr-text bold ${css({ gridColumn: '1 / -1', fontSize: '1.2rem', marginTop: this.spacing.md })}>
          Lieferanweisungen
        </cr-text>
        <cr-flex space="md" horizontal crossaxis-center ${css({ gridColumn: '1 / -1' })}>
          ${this._isEditingShippingInstructions
            ? html`
                <cr-tiptap ${this._form.field('shippingDetails', 'shippingInstructions')}></cr-tiptap>
                ${doneButton(() => (this._isEditingShippingInstructions = false))}
              `
            : html`
                <cr-text>
                  ${this.order.value.shippingDetails.shippingInstructions
                    ? unsafeHTML(this.order.value.shippingDetails.shippingInstructions)
                    : html`Keine`}
                </cr-text>
                ${editButton(() => (this._isEditingShippingInstructions = true))}
              `}
        </cr-flex>
        <cr-text bold ${css({ gridColumn: '1 / -1', fontSize: '1.2rem', marginTop: this.spacing.md })}> </cr-text>
        <cr-text>KPF:</cr-text>
        <cr-text>${this.order.value.shippingDetails.kpf?.produkt.kuerzel ?? '-'}</cr-text>
      </cr-grid>
    `;
  }

  @state() private _isEditingStrasse = false;
  @state() private _isEditingName = false;
  @state() private _isEditingZuHaenden = false;
  @state() private _isEditingZipCity = false;
  private _getOrderRecipientDetails() {
    if (!this.order.value) return nothing;
    const order = this.order.value;

    if (order.shippingDetails.shippingRecipient === LogistikShippingRecipient.SCHOOL)
      return html`
        <cr-flex horizontal crossaxis-center>
          <cr-text>Adresse:</cr-text>
        </cr-flex>
        <cr-flex horizontal crossaxis-center space="sm">
          <cr-text>${order.shippingDetails.shippingSchool?.address?.strasse ?? '-'}</cr-text>
        </cr-flex>
        <cr-flex horizontal crossaxis-center>
          <cr-text>PLZ:</cr-text>
        </cr-flex>
        <cr-flex horizontal crossaxis-center space="sm">
          <cr-text>${order.shippingDetails.shippingSchool?.address?.zipCity ?? '-'}</cr-text>
        </cr-flex>
      `;

    return html`
      <!-- Name -->
      <cr-flex horizontal crossaxis-center>
        <cr-text>Name:</cr-text>
      </cr-flex>
      ${this._isEditingName
        ? html`
            <cr-flex horizontal crossaxis-center space="sm">
              <cr-input ${this._form.field('shippingDetails', 'shippingRecipientName')}></cr-input>
              ${doneButton(() => (this._isEditingName = false))}
            </cr-flex>
          `
        : html`
            <cr-flex horizontal crossaxis-center space="sm">
              <cr-text>${order.shippingDetails.shippingRecipientName ?? '-'}</cr-text>
              ${editButton(() => (this._isEditingName = true))}
            </cr-flex>
          `}
      <!-- Zu Haenden -->
      <cr-flex horizontal crossaxis-center>
        <cr-text>z.H.:</cr-text>
      </cr-flex>
      ${this._isEditingZuHaenden
        ? html`
            <cr-flex horizontal crossaxis-center space="sm">
              <cr-input ${this._form.field('shippingDetails', 'shippingRecipientzuHaenden')}></cr-input>
              ${doneButton(() => (this._isEditingZuHaenden = false))}
            </cr-flex>
          `
        : html`
            <cr-flex horizontal crossaxis-center space="sm">
              <cr-text>${order.shippingDetails.shippingRecipientzuHaenden ?? '-'}</cr-text>
              ${editButton(() => (this._isEditingZuHaenden = true))}
            </cr-flex>
          `}
      <!-- Strasse -->
      <cr-flex horizontal crossaxis-center>
        <cr-text> Adresse: </cr-text>
      </cr-flex>
      ${this._isEditingStrasse
        ? html`
            <cr-flex horizontal crossaxis-center space="sm">
              <cr-input
                placeholder="Straße"
                placeholder-mode="hide"
                ${this._form.field('shippingDetails', 'shippingAddress', 'strasse')}
              ></cr-input>
              <cr-input
                placeholder="Hausnummer"
                placeholder-mode="hide"
                ${this._form.field('shippingDetails', 'shippingAddress', 'hausnummer')}
                ${css({ width: '60px' })}
              ></cr-input>
              ${doneButton(() => (this._isEditingStrasse = false))}
            </cr-flex>
          `
        : html`
            <cr-flex horizontal crossaxis-center space="sm">
              <cr-text>
                ${order.shippingDetails.shippingAddress?.strasse}
                ${order.shippingDetails.shippingAddress?.hausnummer}</cr-text
              >
              ${editButton(() => (this._isEditingStrasse = true))}
            </cr-flex>
          `}
      <!-- PLZ -->
      <cr-flex horizontal crossaxis-center>
        <cr-text>PLZ:</cr-text>
      </cr-flex>
      ${this._isEditingZipCity
        ? html`
            <cr-flex horizontal crossaxis-center space="sm">
              <cr-input
                placeholder="PLZ"
                placeholder-mode="hide"
                ${this._form.field('shippingDetails', 'shippingAddress', 'zipCity', 'zip')}
              ></cr-input>
              <cr-input
                placeholder="Ort"
                placeholder-mode="hide"
                ${this._form.field('shippingDetails', 'shippingAddress', 'zipCity', 'city')}
              ></cr-input>
              ${doneButton(() => (this._isEditingZipCity = false))}
            </cr-flex>
          `
        : html`
            <cr-flex horizontal crossaxis-center space="sm">
              <cr-text>${order.shippingDetails.shippingAddress?.zipCity ?? '-'}</cr-text>
              ${editButton(() => (this._isEditingZipCity = true))}
            </cr-flex>
          `}
    `;
  }

  public override async close() {
    if (this.order.value?.isDirty()) {
      if (!(await ChromiumDialog.confirm('Ungespeicherte Änderungen verwerfen?'))) return;
      if (this.order.value._id) this.order.value.revert();
    }
    void super.close();
  }

  public override getInnerWrapper(options: DialogOptions) {
    const wrapperElem = super.getInnerWrapper(options as Required<DialogOptions>);

    if (!options.draggable) {
      wrapperElem.style.setProperty('max-width', '80vw');
      wrapperElem.style.setProperty('max-height', '100vh');
      wrapperElem.style.setProperty('min-width', '60vw');
    }
    return wrapperElem;
  }
}
